import { useEffect, useState } from 'react';
import type { Accessory } from '@/types/piclick';
import { formatBRL } from '@/lib/price';
import { useCanHoverPreview } from '@/lib/hooks';
import { AccessoryPreview } from './AccessoryPreview';

type Props = {
  accessories: Accessory[];
  selected: string | null;
  onSelect: (id: string) => void;
};

const NONE_ID = 'sem-acessorio';

export function AccessorySection({ accessories, selected, onSelect }: Props) {
  const canHover = useCanHoverPreview();
  const [sheetId, setSheetId] = useState<string | null>(null);
  const [hoveredId, setHoveredId] = useState<string | null>(null);
  const [placement, setPlacement] = useState<'top' | 'bottom'>('top');

  useEffect(() => {
    if (!sheetId) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setSheetId(null);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [sheetId]);

  const sheetAccessory = sheetId
    ? accessories.find((a) => a.id === sheetId) ?? null
    : null;

  const hint = canHover
    ? 'Passe o mouse nos cards para ver a foto real.'
    : 'Toque no ícone da câmera para ver a foto real.';

  const handlePeek = (id: string, el: HTMLElement) => {
    const rect = el.getBoundingClientRect();
    const headerHeight =
      parseFloat(
        getComputedStyle(document.documentElement).getPropertyValue(
          '--pc-header-height'
        )
      ) || 72;
    const POPOVER_HEIGHT = 320;
    const spaceAbove = rect.top - headerHeight - 8;
    const spaceBelow = window.innerHeight - rect.bottom - 8;
    setPlacement(
      spaceAbove >= POPOVER_HEIGHT || spaceAbove >= spaceBelow
        ? 'top'
        : 'bottom'
    );
    setHoveredId(id);
  };

  return (
    <div>
      <p className="mb-3 rounded-xl bg-pink/5 px-3 py-2 text-xs font-medium text-plum/70">
        ✨ {hint}
      </p>

      <ul className="grid grid-cols-3 gap-2 sm:gap-3">
        {accessories.map((accessory) => {
          const isSelected = selected === accessory.id;
          const isNone = accessory.id === NONE_ID;
          const showCameraIcon = !canHover && !isNone;
          const showHoverPopover = canHover && !isNone;

          return (
            <li
              key={accessory.id}
              className="relative"
              onMouseEnter={
                showHoverPopover
                  ? (e) => handlePeek(accessory.id, e.currentTarget)
                  : undefined
              }
              onMouseLeave={showHoverPopover ? () => setHoveredId(null) : undefined}
            >
              <button
                type="button"
                onClick={() => onSelect(accessory.id)}
                onFocus={
                  showHoverPopover
                    ? (e) => handlePeek(accessory.id, e.currentTarget.parentElement as HTMLElement)
                    : undefined
                }
                onBlur={showHoverPopover ? () => setHoveredId(null) : undefined}
                aria-pressed={isSelected}
                className={`relative flex h-full w-full flex-col items-center gap-1.5 rounded-[1.25rem] border-2 px-2 py-3 text-center transition-colors sm:px-3 sm:py-4 ${
                  isSelected
                    ? 'border-plum bg-butter'
                    : 'border-plum/20 bg-white hover:border-plum/50 active:bg-cream'
                }`}
              >
                <span className="flex h-12 w-12 items-center justify-center overflow-hidden rounded-full border-2 border-plum/40 bg-white sm:h-14 sm:w-14">
                  {isNone ? (
                    <CloseGlyph />
                  ) : accessory.imageUrl ? (
                    <img
                      src={accessory.imageUrl}
                      alt=""
                      className="h-10 w-10 rounded-full object-cover sm:h-12 sm:w-12"
                    />
                  ) : (
                    <ChainGlyph />
                  )}
                </span>

                <span className="font-action text-xs font-bold leading-tight text-plum sm:text-sm">
                  {accessory.label}
                </span>

                <span
                  className={`font-action text-xs font-bold tabular-nums leading-none sm:text-sm ${
                    accessory.price > 0 ? 'text-pink' : 'text-plum/60'
                  }`}
                >
                  {accessory.price > 0
                    ? `+${formatBRL(accessory.price)}`
                    : 'Grátis'}
                </span>

                {isSelected && (
                  <span
                    aria-hidden="true"
                    className="absolute right-2 top-2 flex h-5 w-5 items-center justify-center rounded-full bg-plum text-[10px] font-bold text-white"
                  >
                    ✓
                  </span>
                )}
              </button>

              {showCameraIcon && (
                <button
                  type="button"
                  aria-label={`Ver foto de ${accessory.label}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    setSheetId(accessory.id);
                  }}
                  className="absolute left-2 top-2 z-10 flex h-7 w-7 items-center justify-center rounded-full bg-white text-plum shadow-[0_2px_6px_rgba(96,11,46,0.18)] active:scale-95"
                >
                  <CameraGlyph />
                </button>
              )}

              {showHoverPopover && hoveredId === accessory.id && (
                <div
                  className={`pointer-events-none absolute left-1/2 z-50 w-64 -translate-x-1/2 ${
                    placement === 'top' ? 'bottom-full mb-2' : 'top-full mt-2'
                  }`}
                >
                  <AccessoryPreview accessory={accessory} />
                </div>
              )}
            </li>
          );
        })}
      </ul>

      {sheetAccessory && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label={`Foto de ${sheetAccessory.label}`}
          className="fixed inset-0 z-50 flex items-end justify-center bg-plum/60 p-4 pb-[max(1rem,env(safe-area-inset-bottom))] sm:items-center"
          onClick={() => setSheetId(null)}
        >
          <div
            className="w-full max-w-sm"
            onClick={(e) => e.stopPropagation()}
          >
            <AccessoryPreview accessory={sheetAccessory} />
            <button
              type="button"
              onClick={() => setSheetId(null)}
              className="mt-3 w-full rounded-full border-[3px] border-plum bg-white px-6 py-2.5 font-action text-sm font-bold text-plum shadow-[0_5px_0_0_#600b2e] active:translate-y-[3px] active:shadow-[0_2px_0_0_#600b2e]"
            >
              Fechar
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function CloseGlyph() {
  return (
    <svg
      width="22"
      height="22"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      className="text-plum/70"
      aria-hidden="true"
    >
      <line x1="6" y1="6" x2="18" y2="18" />
      <line x1="18" y1="6" x2="6" y2="18" />
    </svg>
  );
}

function ChainGlyph() {
  return (
    <svg
      width="22"
      height="22"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      className="text-plum/70"
      aria-hidden="true"
    >
      <circle cx="7" cy="7" r="3" />
      <circle cx="17" cy="17" r="3" />
      <line x1="9" y1="9" x2="15" y2="15" />
    </svg>
  );
}

function CameraGlyph() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.4"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M3 7h4l2-2h6l2 2h4v12H3z" />
      <circle cx="12" cy="13" r="3.5" />
    </svg>
  );
}
